import type { AppVueRouter } from '@modules/core/services/router/types';
import type { TranslationKeysMapper } from '@modules/i18n/types';
import type { AppMenu } from '@modules/sidebar/utils';
import { AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/constants';
import {
  AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/constants';
import { AppModule } from '@modules/core/plugins/AppModule';
import { useI18n } from '@modules/i18n/composables';
import {
  ADMIN_PARTNERSHIP_MENU_ITEM,
  TRANSLATION_GROUP_SIDEBAR,
} from '@modules/sidebar/constants';

import {
  AFFILIATE_PROGRAM_TRACKERS_DEAL_SETTINGS_HISTORY_CHANGES_EDIT_ROUTE_NAME,
  AFFILIATE_PROGRAM_TRACKERS_DEAL_SETTINGS_MAIN_ROUTE_NAME,
  AFFILIATE_PROGRAM_TRACKERS_FIXED_PAYMENTS_HISTORY_EDIT_ROUTE_NAME,
  AFFILIATE_PROGRAM_TRACKERS_FIXED_PAYMENTS_ROUTE_NAME,
  AFFILIATE_PROGRAM_TRACKERS_LIST_QUERY_KEY,
  AFFILIATE_PROGRAM_TRACKERS_LIST_ROUTE_NAME,
  AFFILIATE_PROGRAM_TRACKERS_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATE_PROGRAM_TRACKERS,
} from './constants';
import { getTranslationKeys } from './translations';

export class AffiliateProgramTrackersModule extends AppModule {
  addRoutes(router: AppVueRouter): void {
    // Trackers list
    router.addAdminRoute({
      name: AFFILIATE_PROGRAM_TRACKERS_LIST_ROUTE_NAME,
      path: 'trackers',
      permission: AFFILIATE_PROGRAM_TRACKERS_PERMISSIONS.SHOW,
      component: () => import('./pages/AffiliateProgramTrackersListPage/AffiliateProgramTrackersListPage.vue'),
    });

    router.addChildRoute(AFFILIATE_PROGRAM_TRACKERS_LIST_ROUTE_NAME, {
      name: AFFILIATE_PROGRAM_TRACKERS_FIXED_PAYMENTS_ROUTE_NAME,
      path: ':id/:trackerId/fixed-tracker-payments',

      component: () => import('@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/pages/FixedTrackerPaymentsModalPage/FixedTrackerPaymentsModalPage.vue'),
      permission: AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS.HISTORY_CHANGES_EDIT,
      props: {
        backTo: { name: AFFILIATE_PROGRAM_TRACKERS_LIST_ROUTE_NAME },
        editHistoryRoute: { name: AFFILIATE_PROGRAM_TRACKERS_FIXED_PAYMENTS_HISTORY_EDIT_ROUTE_NAME },
        backFromEditHistoryRoute: { name: AFFILIATE_PROGRAM_TRACKERS_FIXED_PAYMENTS_ROUTE_NAME },
        trackersListQueryKey: AFFILIATE_PROGRAM_TRACKERS_LIST_QUERY_KEY,
      },
    });

    router.addChildRoute(AFFILIATE_PROGRAM_TRACKERS_FIXED_PAYMENTS_ROUTE_NAME, {
      name: AFFILIATE_PROGRAM_TRACKERS_FIXED_PAYMENTS_HISTORY_EDIT_ROUTE_NAME,
      path: ':merchantId/history/:itemId',
      permission: AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.HISTORY_CHANGES_EDIT,

      component: () => import('@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/submodules/fixed-payments-history-edit/pages/FixedPaymentsHistoryEditPage/FixedPaymentsHistoryEditPage.vue'),
    });

    router.addChildRoute(AFFILIATE_PROGRAM_TRACKERS_LIST_ROUTE_NAME, {
      name: AFFILIATE_PROGRAM_TRACKERS_DEAL_SETTINGS_MAIN_ROUTE_NAME,
      path: ':id/:trackerId/conditions',

      component: () => import('@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/tracker-deal-changes/pages/TrackerDealChangesModalPage/TrackerDealChangesModalPage.vue'),
      props: {
        backTo: { name: AFFILIATE_PROGRAM_TRACKERS_LIST_ROUTE_NAME },
        editHistoryRoute: { name: AFFILIATE_PROGRAM_TRACKERS_DEAL_SETTINGS_HISTORY_CHANGES_EDIT_ROUTE_NAME },
        backFromEditHistoryRoute: { name: AFFILIATE_PROGRAM_TRACKERS_DEAL_SETTINGS_MAIN_ROUTE_NAME },
        trackersListQueryKey: AFFILIATE_PROGRAM_TRACKERS_LIST_QUERY_KEY,
      },
    });

    router.addChildRoute(AFFILIATE_PROGRAM_TRACKERS_DEAL_SETTINGS_MAIN_ROUTE_NAME, {
      name: AFFILIATE_PROGRAM_TRACKERS_DEAL_SETTINGS_HISTORY_CHANGES_EDIT_ROUTE_NAME,
      path: ':merchantId/history/:itemId',
      permission: AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.HISTORY_CHANGES_EDIT,

      component: () => import('@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/tracker-deal-changes/submodules/tracker-history-changes-edit/pages/TrackerHistoryChangesEditPage/TrackerHistoryChangesEditPage.vue'),
    });
  }

  addMenu(menu: AppMenu): void {
    const { getTPath } = useI18n();

    menu.addChildAdminMenuItem(ADMIN_PARTNERSHIP_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'ADMIN_PARTNERSHIP_TRACKERS'),
      to: {
        name: AFFILIATE_PROGRAM_TRACKERS_LIST_ROUTE_NAME,
      },
      permission: AFFILIATE_PROGRAM_TRACKERS_PERMISSIONS.SHOW,
      id: AFFILIATE_PROGRAM_TRACKERS_LIST_ROUTE_NAME,
      order: 10,
    });
  }

  addTranslationKeys(mapper: TranslationKeysMapper): void {
    mapper.addTranslationsMap(TRANSLATION_GROUP_AFFILIATE_PROGRAM_TRACKERS, getTranslationKeys);
  }
}
